import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Button, TextField, ButtonGroup, ClickAwayListener, FormControl, Grow, InputLabel, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper, Select } from '@mui/material';
import { startOfMonth, subMonths } from 'date-fns';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ArrowDropDownRounded, FileDownloadOutlined, SyncRounded } from '@mui/icons-material';

const defaultStartDate = startOfMonth(subMonths(new Date(), 1)), defaultEndDate = new Date();

const DateRangeFilter = ({loadData, startDate, endDate, downloadFile, filterByOptions}) => {

  const showFilterBy = Array.isArray(filterByOptions) && filterByOptions.length > 0;
  const defaultFilterValues = useMemo(() => ({from: startDate || defaultStartDate, to: endDate || defaultEndDate, ...(showFilterBy ? {filterBy: filterByOptions[0].value} : {})}), [startDate, endDate, filterByOptions, showFilterBy])
  const [filters, setFilters] = useState(defaultFilterValues);
  const [openMoreButtons, setOpenMoreButtons] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    typeof loadData === 'function' && loadData(defaultFilterValues);
  }, [defaultFilterValues, loadData]);
  
  function onFetchBtnClick() {
    typeof loadData === 'function' && loadData(filters);
  }

  const handleClose = (event) => {
    if (anchorRef?.current && anchorRef?.current?.contains(event.target)) {
      return;
    }
    setOpenMoreButtons(false);
  };

  const handleToggle = () => {
    setOpenMoreButtons((prevOpen) => !prevOpen);
  };

  function onDownloadFileBtnClick(e) {
    handleToggle();
    typeof downloadFile === 'function' && setTimeout(downloadFile, 200);
  }
  
  return (
    <Grid item container xs={12} justifyContent="space-between" spacing={1} alignItems="center" >
      <Grid item container xs={12} sm={8} md={8} xl={8} spacing={2} >
        <Grid item xs={showFilterBy ? 12 : 6} sm={showFilterBy ? 4 : 6} md={showFilterBy ? 4 : 6} lg={showFilterBy ? 4 : 5} xl={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} >
            <DatePicker
              required
              label="Start date"
              format="dd-MMM-yyyy"
              clearable={false}
              value={filters.from}
              maxDate={filters.to}
              onChange={(value) => setFilters(prevDate => ({...prevDate, from: value}))}
              openTo="day"
              views={['year', 'month', 'day']}
              textField={(params) => (
                <TextField 
                  value={filters.from}
                  {...params} 
                  margin="normal" 
                  sx={{width: '100%'}}
                />
              )}
              slotProps={{
                actionBar: {
                  actions: ['today'],
                },
                textField: {
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={showFilterBy ? 12 : 6} sm={showFilterBy ? 4 : 6} md={showFilterBy ? 4 : 6} lg={showFilterBy ? 4 : 5} xl={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} >
            <DatePicker
              required
              label="End date"
              format="dd-MMM-yyyy"
              clearable={false}
              value={filters.to}
              minDate={filters.from}
              disableFuture
              onChange={(value) => setFilters(prevDate => ({...prevDate, to: value}))}
              openTo="day"
              views={['year', 'month', 'day']}
              textField={(params) => (
                <TextField 
                  value={filters.to}
                  {...params} 
                  margin="normal" 
                  sx={{width: '100%'}}
                />
              )}
              slotProps={{
                actionBar: {
                  actions: ['today'],
                },
                textField: {
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        {showFilterBy ? <Grid item xs={12} sm={4} md={4} lg={4} xl={3}>
          <FormControl fullWidth>
            <InputLabel id="filter-by-label">FilterBy</InputLabel>
            <Select
              label="FilterBy"
              labelId="filter-by-label"
              onChange={(event) => setFilters((previousFilters) => ({...previousFilters, filterBy: event.target.value}))}
              value={filters.filterBy}
            >
              {filterByOptions.map(({label, value}) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid> : null}
      </Grid>
      <Grid item xs={12} sm={4} md={4} xl={4} textAlign="right" >
        <>
          <ButtonGroup variant="contained" ref={anchorRef} color="tertiary" sx={{position: 'relative'}}>
            <Button sx={{borderRadius: 1}} startIcon={<SyncRounded />} variant="tonal" onClick={onFetchBtnClick}>Refresh</Button>
            <Button
              size="small"
              aria-expanded={openMoreButtons ? 'true' : undefined}
              aria-haspopup="menu"
              onClick={handleToggle}
              sx={{borderTopRightRadius: '2px !important', borderBottomRightRadius: '2px !important'}}
            >
              <ArrowDropDownRounded  />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{
              zIndex: 1,
            }}
            open={openMoreButtons}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu" autoFocusItem>
                        <MenuItem onClick={onDownloadFileBtnClick}>
                          <ListItemIcon>
                            <FileDownloadOutlined  />
                          </ListItemIcon>
                          <ListItemText>Download Report</ListItemText>
                        </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      </Grid>
    </Grid>
  );
}
 
export default  memo(DateRangeFilter);
